import { getDoseComponents } from "@/lib/dose-components";
import {
  formatDate,
  formatName,
  formatPhoneNumber,
  formatUnit,
} from "@/lib/format-data";

import { Unit } from "@/types/enums/unit";
import { MedicationSummary } from "@/types/medication";
import { PatientSummary } from "@/types/patient";
import { PrivateTenant } from "@/types/tenant";

/**
 * Fixed physical geometry of the DYMO 30252 address label (1.125" x 3.5"),
 * printed landscape. Horizontal padding must clear the LabelWriter's ~0.207"
 * unprintable margin at each leading/trailing edge, or text is clipped.
 */
export const LABEL_GEOMETRY = {
  widthIn: 3.5,
  heightIn: 1.125,
  padXIn: 0.23,
  /** Extra top clearance so the first line isn't clipped by the LabelWriter's
   *  top unprintable edge; bottom stays tight so the fill isn't wasted. */
  padTopIn: 0.1,
  padBottomIn: 0.05,
  /** Ceiling for auto-fit; text grows toward this to fill the label, then
   *  shrinks from here for dense content. Set high so sparse labels don't
   *  print tiny with the bottom half empty. */
  fontMaxPt: 12,
  /** Readability floor. Below this we warn instead of shrinking further. */
  fontMinPt: 4.4,
  fontStepPt: 0.1,
} as const;

/** Every field that appears on the label. All are editable at print time. */
export interface MedicationLabelData {
  clinicName: string;
  clinicAddress: string;
  clinicPhone: string;
  npi: string;
  dea: string;
  patientName: string;
  dob: string;
  patientPhone: string;
  patientId: string;
  /** Shipping / patient address line. Pre-filled from the patient record. */
  shippingAddress: string;
  medication: string;
  disp: string;
  refill: string;
  ndc: string;
  sig: string;
  dispDate: string;
  rxDate: string;
  exp: string;
  lot: string;
}

function escapeHtml(value: string): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** Shared stylesheet for both the on-screen preview and the print window. */
export const LABEL_CSS = `
  * { box-sizing: border-box; }
  .dymo-label {
    width: ${LABEL_GEOMETRY.widthIn}in;
    height: ${LABEL_GEOMETRY.heightIn}in;
    padding: ${LABEL_GEOMETRY.padTopIn}in ${LABEL_GEOMETRY.padXIn}in ${LABEL_GEOMETRY.padBottomIn}in;
    overflow: hidden;
    background: #fff;
    color: #000;
    font-family: "Helvetica Neue", Arial, sans-serif;
  }
  .dymo-label .content { line-height: 1.24; overflow-wrap: anywhere; }
  .dymo-label .row { display: flex; justify-content: space-between; gap: 5px; }
  .dymo-label .row .r { text-align: right; }
  .dymo-label .bold { font-weight: 700; }
  .dymo-label .nowrap { white-space: nowrap; }
  /* Each meta segment stays intact but the group wraps between segments, so a
     long value (e.g. a raw PtID) breaks onto a new line instead of forcing the
     auto-fit to shrink the whole label to fit one wide, unbreakable line. */
  .dymo-label .seg { white-space: nowrap; }
  .dymo-label .med { font-weight: 700; margin-top: 1px; }
  .dymo-label .sig { margin-top: 1px; }
  .dymo-label .footer { margin-top: 1px; display: flex; flex-wrap: wrap; justify-content: space-between; gap: 1px 10px; }
  .dymo-label .footer > span { white-space: nowrap; }
  .dymo-label .signoff { margin-top: 2px; display: flex; gap: 8px; white-space: nowrap; }
  .dymo-label .signoff .sf { flex: 1; display: flex; align-items: flex-end; gap: 2px; }
  .dymo-label .signoff .ln { flex: 1; border-bottom: 0.5pt solid #000; height: 0.8em; }
`;

/**
 * The label's inner content (everything inside `.content`). Shared verbatim by
 * the preview and the print document so what staff see is what prints.
 */
export function buildLabelContentHtml(d: MedicationLabelData): string {
  const esc = escapeHtml;
  const row = (left: string, right: string) =>
    `<div class="row"><span>${left}</span>${
      right ? `<span class="r">${right}</span>` : ""
    }</div>`;

  // Wrap each segment in a nowrap span so the group breaks *between* segments
  // (never mid-segment), instead of joining with non-breaking spaces — which
  // made one long value force the auto-fit to shrink the whole label.
  const segs = (parts: (string | false | undefined)[]) =>
    parts
      .filter(Boolean)
      .map((s) => `<span class="seg">${s}</span>`)
      .join(" ");

  const ptMeta = segs([
    d.dob && `DOB: ${esc(d.dob)}`,
    d.patientPhone && `Ph: ${esc(d.patientPhone)}`,
    d.patientId && `PtID: ${esc(d.patientId)}`,
  ]);

  // The med name can be long (multiple components) so leave it wrappable; the
  // short trailing fields stay intact as segments.
  const medLine = [
    esc(d.medication),
    segs([
      d.disp && `Disp: ${esc(d.disp)}`,
      d.refill && `Refill: ${esc(d.refill)}`,
      d.ndc && `NDC#: ${esc(d.ndc)}`,
    ]),
  ]
    .filter(Boolean)
    .join(" ");

  const footer = [
    d.dispDate && `<span>Disp Date: ${esc(d.dispDate)}</span>`,
    d.rxDate && `<span>Rx Date: ${esc(d.rxDate)}</span>`,
    d.exp && `<span>Exp: ${esc(d.exp)}</span>`,
    d.lot && `<span>Lot: ${esc(d.lot)}</span>`,
  ]
    .filter(Boolean)
    .join("");

  // Only emit a line when it has content, so missing fields don't leave blank
  // gaps. The signature line always prints (its blanks are meant to be signed).
  // NPI first (always relevant), then DEA (only for controlled substances).
  const idMeta = segs([
    d.npi && `NPI: ${esc(d.npi)}`,
    d.dea && `DEA: ${esc(d.dea)}`,
  ]);
  const lines: string[] = [];
  if (d.clinicName || idMeta) {
    lines.push(row(`<span class="bold">${esc(d.clinicName)}</span>`, idMeta));
  }
  if (d.clinicAddress || d.clinicPhone) {
    lines.push(
      row(
        esc(d.clinicAddress),
        d.clinicPhone ? `O: ${esc(d.clinicPhone)}` : "",
      ),
    );
  }
  if (d.patientName || ptMeta) {
    lines.push(
      row(`<span class="bold nowrap">${esc(d.patientName)}</span>`, ptMeta),
    );
  }
  if (d.shippingAddress)
    lines.push(`<div>Ship: ${esc(d.shippingAddress)}</div>`);
  if (medLine) lines.push(`<div class="med">${medLine}</div>`);
  if (d.sig) lines.push(`<div class="sig">${esc(d.sig)}</div>`);
  if (footer) lines.push(`<div class="footer">${footer}</div>`);
  lines.push(
    `<div class="signoff"><span class="sf">DR:<span class="ln"></span></span><span class="sf">MOA:<span class="ln"></span></span><span class="sf">PT:<span class="ln"></span></span></div>`,
  );
  return lines.join("");
}

/**
 * Shrink the content font from `fontMaxPt` until it fits the available box
 * (both height AND width — some lines are nowrap and would otherwise clip off
 * the edge), stopping at `fontMinPt`. Returns the chosen size and whether it
 * still overflows at the floor (caller warns the user to shorten the text).
 * Used by both the preview (called directly) and the print window (inlined).
 */
export function fitLabelFont(
  contentEl: HTMLElement,
  availableHeightPx: number,
  availableWidthPx: number,
): { fontPt: number; overflows: boolean } {
  const { fontMaxPt, fontMinPt, fontStepPt } = LABEL_GEOMETRY;
  // 1px width tolerance: the signoff's flex underscores always stretch to fill
  // the content box, so integer scrollWidth rounds ~1px over availableWidth on
  // every label. Without the slack the width check fires perpetually and shrinks
  // every label to the floor, leaving the bottom half blank.
  const overflowing = () =>
    contentEl.scrollHeight > availableHeightPx ||
    contentEl.scrollWidth > availableWidthPx + 1;
  let size = fontMaxPt;
  contentEl.style.fontSize = `${size}pt`;
  while (overflowing() && size > fontMinPt) {
    size = Math.round((size - fontStepPt) * 10) / 10;
    contentEl.style.fontSize = `${size}pt`;
  }
  return { fontPt: size, overflows: overflowing() };
}

/**
 * Full standalone HTML document for the print window: @page sized to the
 * physical label, the shared content, and an inline auto-fit script that runs
 * before the browser's print dialog fires.
 */
export function buildLabelPrintDocument(d: MedicationLabelData): string {
  const {
    widthIn,
    heightIn,
    padTopIn,
    padBottomIn,
    fontMaxPt,
    fontMinPt,
    fontStepPt,
  } = LABEL_GEOMETRY;
  const title = `Label - ${d.patientName || "Medication"}`;
  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(title)}</title>
    <style>
      @page { size: ${widthIn}in ${heightIn}in; margin: 0; }
      html, body { margin: 0; padding: 0; }
      body { width: ${widthIn}in; height: ${heightIn}in; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      ${LABEL_CSS}
    </style>
  </head>
  <body>
    <div class="dymo-label"><div class="content" id="dymo-content">${buildLabelContentHtml(
      d,
    )}</div></div>
    <script>
      (function () {
        var box = document.querySelector(".dymo-label");
        var c = document.getElementById("dymo-content");
        var availH = box.clientHeight - (${padTopIn} + ${padBottomIn}) * 96;
        var availW = box.clientWidth - ${LABEL_GEOMETRY.padXIn} * 96 * 2;
        var size = ${fontMaxPt}, min = ${fontMinPt};
        function over() { return c.scrollHeight > availH || c.scrollWidth > availW + 1; }
        c.style.fontSize = size + "pt";
        while (over() && size > min) {
          size = Math.round((size - ${fontStepPt}) * 10) / 10;
          c.style.fontSize = size + "pt";
        }
      })();
    </script>
  </body>
</html>`;
}

/** Single-line address from the parts a patient/user record carries. */
export function formatAddressLine(parts: {
  address?: string | null;
  addressLineTwo?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
}): string {
  const cityStateZip = [
    parts.city?.trim(),
    [parts.state?.trim(), parts.zipCode?.trim()].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");
  return [parts.address?.trim(), parts.addressLineTwo?.trim(), cityStateZip]
    .filter(Boolean)
    .join(", ");
}

/** Human-readable "Drug Name amount unit" for each active dose component. */
function formatMedicationLine(medication: MedicationSummary): string {
  const components = getDoseComponents(medication);
  return components
    .map((c) => {
      const amount =
        c.amount != null
          ? `${c.amount}${c.unit ? formatUnit(c.unit as Unit, true) : ""}`
          : "";
      return [c.name?.trim(), amount].filter(Boolean).join(" ");
    })
    .filter(Boolean)
    .join(" / ");
}

/** Resolved values the dialog looks up (provider DEA, administration location, lot). */
export interface LabelPrefillContext {
  /** Prescriber name + credentials, shown before the clinic on the header line. */
  prescriber?: string | null;
  /** NPI of the prescriber (administering provider, or one picked in the dialog). */
  npi?: string | null;
  /** DEA of the prescriber (administering provider, or one picked in the dialog). */
  dea?: string | null;
  /** Location where the dose was administered — its name + address become the clinic lines. */
  location?: {
    name?: string | null;
    addressLineOne?: string | null;
    addressLineTwo?: string | null;
    city?: string | null;
    state?: string | null;
    zipCode?: string | null;
    phoneNumber?: string | null;
  } | null;
  /** NDC + expiration from the dispensed lot's shipment. */
  ndc?: string | null;
  exp?: string | null;
}

/**
 * Best-effort pre-fill from the dose, patient, tenant, and resolved context.
 * Anything still unknown comes back empty for the user to fill in the dialog.
 */
/** Snapshot lot codes/expiry come from the dose; shipment metadata is keyed only by identity. */
export function resolveDoseLabelLotMetadata(
  medication: MedicationSummary,
  shipments: Array<{ id: string; ndc?: string | null }>,
): { ndc: string | undefined; expiryDate: string | undefined } {
  const lots = getDoseComponents(medication).flatMap(
    (component) => component.lots ?? [],
  );
  const matched = lots.map((lot) =>
    shipments.find((shipment) => shipment.id === lot.shipmentId),
  );
  const stockIds = new Set(lots.map((lot) => lot.stockId));
  const ndcs = new Set(matched.map((shipment) => shipment?.ndc));
  return {
    ndc:
      lots.length > 0 &&
      stockIds.size === 1 &&
      !stockIds.has(null) &&
      matched.every(Boolean) &&
      ndcs.size === 1
        ? ([...ndcs][0] ?? undefined)
        : undefined,
    expiryDate: lots
      .map((lot) => lot.expiryDate)
      .filter((value): value is string => !!value)
      .sort()[0],
  };
}

export function buildInitialLabelData(
  medication: MedicationSummary,
  patient: PatientSummary,
  tenant: PrivateTenant | null,
  ctx: LabelPrefillContext = {},
): MedicationLabelData {
  const components = getDoseComponents(medication);
  const sig = [medication.frequency?.trim(), medication.comment?.trim()]
    .filter(Boolean)
    .join(" ");

  const clinicPhone = ctx.location?.phoneNumber || tenant?.phoneNumber || "";

  return {
    // Header line: prescriber (name + creds), then tenant name, then location name.
    clinicName: [ctx.prescriber, tenant?.name, ctx.location?.name]
      .filter(Boolean)
      .join("  "),
    clinicAddress: ctx.location
      ? formatAddressLine({
          address: ctx.location.addressLineOne,
          addressLineTwo: ctx.location.addressLineTwo,
          city: ctx.location.city,
          state: ctx.location.state,
          zipCode: ctx.location.zipCode,
        })
      : "",
    clinicPhone: clinicPhone ? formatPhoneNumber(clinicPhone) : "",
    npi: ctx.npi ?? "",
    dea: ctx.dea ?? "",
    patientName: [patient.lastName, patient.firstName]
      .filter(Boolean)
      .map((part) => formatName(part, undefined))
      .join(", "),
    dob: patient.dateOfBirth ? formatDate(patient.dateOfBirth) : "",
    patientPhone: patient.phoneNumber
      ? formatPhoneNumber(patient.phoneNumber)
      : "",
    patientId: patient.id ?? "",
    shippingAddress: formatAddressLine(patient),
    medication: formatMedicationLine(medication),
    disp: "",
    refill: "",
    ndc: ctx.ndc ?? "",
    sig,
    dispDate: formatDate(new Date()),
    rxDate: medication.doseDate
      ? formatDate(medication.doseDate)
      : formatDate(medication.createdDate),
    exp: ctx.exp ?? "",
    lot: components
      .flatMap((component) => component.lots ?? [])
      .map((lot) => lot.lotNumber)
      .filter(Boolean)
      .join(", "),
  };
}
